import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Card, List, Button, Avatar, Typography, Empty, Space } from "antd";
import { CalendarOutlined, FileTextOutlined } from "@ant-design/icons";
import { formatDateOnly } from "Common/Components/FormatDateTime";
import { useContext } from "react";
import { ProfileContext } from "Common/hooks/context/ProfileContext";

const { Text } = Typography;

const DeadlinesList = ({ darkMode, upcomingDeadlines }) => {
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;
  const navigate = useNavigate();

  const theme = {
    cardBg: darkMode ? "#1f1f1f" : "#fff",
    border: darkMode ? "#303030" : "#f0f0f0",
    text: darkMode ? "rgba(255, 255, 255, 0.85)" : undefined,
    secondaryText: darkMode ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.45)",
    priorityHigh: darkMode ? "#ff4d4f" : "#ff4d4f",
    priorityMedium: darkMode ? "#faad14" : "#faad14",
    priorityLow: darkMode ? "#52c41a" : "#52c41a"
  };

  const getPriorityStyle = (priority) => {
    const bgColors = {
      high: darkMode ? "#ff4d4f33" : "#fff2f0",
      medium: darkMode ? "#faad1433" : "#fffbe6",
      low: darkMode ? "#52c41a33" : "#f6ffed"
    };
    const textColors = {
      high: theme.priorityHigh,
      medium: theme.priorityMedium,
      low: theme.priorityLow
    };
    return {
      backgroundColor: bgColors[priority] || bgColors.low,
      color: textColors[priority] || textColors.low
    };
  };

  return (
    <Card
      title={
        <Text strong style={{ color: theme.text }}>
          <CalendarOutlined style={{ marginRight: 8 }} /> Upcoming Deadlines
        </Text>
      }
      style={{
        borderRadius: 8,
        backgroundColor: theme.cardBg,
        borderColor: theme.border,
      }}
      styles={{ body: { padding: '12px' } }}
    >
      {upcomingDeadlines?.length > 0 ? (
        <>
          <List
            dataSource={upcomingDeadlines.slice(0, 2)}
            renderItem={(item) => (
              <List.Item style={{ padding: '8px 0' }}>
                <Space align="start" size={12}>
                  <Avatar 
                    style={getPriorityStyle(item.priority)}
                    icon={<FileTextOutlined />}
                    size="small"
                  />
                  <div>
                    <Text strong style={{ 
                      color: theme.text,
                      display: 'block',
                      fontSize: '14px'
                    }}>
                      {item.title}
                    </Text>
                    <Text type="secondary" style={{ 
                      fontSize: '12px',
                      display: 'block'
                    }}>
                      {item.trainingName} • Due: {formatDateOnly(item.dueDate)}
                    </Text>
                  </div>
                </Space>
              </List.Item>
            )}
          />
          <Button
            type="link"
            size="small"
            block
            disabled={isBlocked}
            onClick={() => !isBlocked && navigate("/all-deadlines", { state: { darkMode, upcomingDeadlines } })}
            style={{ 
              marginTop: '8px',
              color: theme.text
            }}
          >
            View All Deadlines
          </Button>
        </>
      ) : (
        <Empty
          description={
            <Text type="secondary" style={{ color: theme.secondaryText }}>
              No upcoming deadlines
            </Text>
          }
          imageStyle={{ height: 40 }}
        />
      )}
    </Card>
  );
};

DeadlinesList.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  upcomingDeadlines: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      trainingName: PropTypes.string.isRequired,
      dueDate: PropTypes.string.isRequired,
      priority: PropTypes.string.isRequired,
    })
  ),
};

export default DeadlinesList;
